<script>
  import { onMount } from "svelte";
  import {
    Page,
    Card,
    CardHeader,
    CardContent,
    Block,
    Searchbar,
    Button,
    Badge,
    Icon,
    f7,
  } from "framework7-svelte";
  import { BASE_URL } from "../services/service.config";
  import { TradingService } from "../services/tradingService";
  import LiveSignalChartModal from "../components/LiveSignalChartModal.svelte";
  import TradingNavbar from "../components/TradingNavbar.svelte";

  let activeTab = "s3s4"; // "s3s4" or "tokens"
  let searchQuery = "";
  let statusFilter = "all"; // "all", "active", "disabled"

  let showChartModal = false;
  let chartSymbol = "";
  let chartScripCode = "";

  function openScripChart(instrument, bseToken) {
    chartSymbol = instrument;
    chartScripCode = String(bseToken || "");
    showChartModal = true;
  }

  // /s3s4 F1F2 Company Array Scrip Matrix
  let companyScrips = [];
  let isLoadingCompanyArray = false;
  let companyArrayError = null;
  let savingRowId = null;

  // Master Token Directory
  let scrips = [];
  let isLoadingCatalog = false;
  let catalogError = null;

  async function loadCompanyArray() {
    isLoadingCompanyArray = true;
    companyArrayError = null;
    try {
      const res = await TradingService.fetchCompanyArray({ limit: 500 });
      const list = res.data || [];
      companyScrips = list.map((s) => {
        const item = {
          id: s.id,
          instrument: s.instrument || s.Stock_Name || "",
          name: s.name || s.Stock_Name || "",
          bse_token: s.bse_token || s.Security_Code || "",
          trading_st: Number(s.trading_st) === 1 ? 1 : 0,
          H_qty: s.H_qty !== undefined ? Number(s.H_qty) : 5,
          M_qty: s.M_qty !== undefined ? Number(s.M_qty) : 2,
          L_qty: s.L_qty !== undefined ? Number(s.L_qty) : 1,
          bqty_st: s.bqty_st !== undefined ? Number(s.bqty_st) : 3,
          sqty_st: s.sqty_st !== undefined ? Number(s.sqty_st) : 0,
          buy_target_intra: s.buy_target_intra !== undefined ? Number(s.buy_target_intra) : 1.0,
          buy_stl_intra: s.buy_stl_intra !== undefined ? Number(s.buy_stl_intra) : 1.0,
          Trailing_Stoploss: s.Trailing_Stoploss !== undefined ? Number(s.Trailing_Stoploss) : 0.2,
        };
        item._orig = {
          H_qty: item.H_qty,
          M_qty: item.M_qty,
          L_qty: item.L_qty,
          bqty_st: item.bqty_st,
          sqty_st: item.sqty_st,
          buy_target_intra: item.buy_target_intra,
          buy_stl_intra: item.buy_stl_intra,
          Trailing_Stoploss: item.Trailing_Stoploss,
        };
        return item;
      });
    } catch (err) {
      console.error("Error loading F1F2 company array:", err);
      companyScrips = [];
      companyArrayError = err.message || "Failed to load strategy matrix";
    } finally {
      isLoadingCompanyArray = false;
    }
  }

  async function loadCatalogScrips() {
    isLoadingCatalog = true;
    catalogError = null;
    try {
      const res = await fetch(`${BASE_URL}/api/v1/master-data/scrips?limit=200`);
      if (!res.ok) {
        throw new Error(`Server returned HTTP ${res.status}: ${res.statusText}`);
      }
      const json = await res.json();
      const list = json.data || json.scrips || [];
      scrips = list.map((s) => ({
        id: s.id || s._id || s.scrip_code,
        name: s.company_name || s.name || s.symbol,
        symbol: s.symbol || s.scrip_symbol || "-",
        token: s.internal_token || s.token || "-",
        bse_token: s.scrip_code || s.bse_token || s.bse_code || "-",
        category: s.category || s.sector || "Equity",
      }));
    } catch (err) {
      console.error("Error loading scrip catalog:", err);
      scrips = [];
      catalogError = err.message || "Failed to load scrip catalog";
    } finally {
      isLoadingCatalog = false;
    }
  }

  function isRowModified(row) {
    if (!row || !row._orig) return false;
    const fields = [
      "H_qty",
      "M_qty",
      "L_qty",
      "bqty_st",
      "sqty_st",
      "buy_target_intra",
      "buy_stl_intra",
      "Trailing_Stoploss",
    ];
    for (const f of fields) {
      const cur = row[f];
      const orig = row._orig[f];
      if (cur === "" || cur === null || cur === undefined) {
        if (orig !== "" && orig !== null && orig !== undefined) return true;
      } else {
        if (Number(cur) !== Number(orig)) return true;
      }
    }
    return false;
  }

  async function saveScripMatrixRow(row) {
    savingRowId = row.id;
    try {
      const payload = {
        trading_st: row.trading_st ? 1 : 0,
        H_qty: Number(row.H_qty) || 1,
        M_qty: Number(row.M_qty) || 1,
        L_qty: Number(row.L_qty) || 1,
        bqty_st: Number(row.bqty_st) || 0,
        sqty_st: Number(row.sqty_st) || 0,
        buy_target_intra: Number(row.buy_target_intra) || 1.0,
        buy_stl_intra: Number(row.buy_stl_intra) || 1.0,
        Trailing_Stoploss: Number(row.Trailing_Stoploss) || 0.2,
      };
      const res = await TradingService.updateCompanyArrayScrip(row.id, payload);

      // Sync row values with saved payload and reset baseline
      row.H_qty = payload.H_qty;
      row.M_qty = payload.M_qty;
      row.L_qty = payload.L_qty;
      row.bqty_st = payload.bqty_st;
      row.sqty_st = payload.sqty_st;
      row.buy_target_intra = payload.buy_target_intra;
      row.buy_stl_intra = payload.buy_stl_intra;
      row.Trailing_Stoploss = payload.Trailing_Stoploss;
      row._orig = {
        H_qty: payload.H_qty,
        M_qty: payload.M_qty,
        L_qty: payload.L_qty,
        bqty_st: payload.bqty_st,
        sqty_st: payload.sqty_st,
        buy_target_intra: payload.buy_target_intra,
        buy_stl_intra: payload.buy_stl_intra,
        Trailing_Stoploss: payload.Trailing_Stoploss,
      };
      companyScrips = [...companyScrips];

      f7.toast.create({
        text: `✓ ${row.instrument} saved! Engine registry hot-reloaded: ${res.engine_reloaded ? "Yes" : "OK"}`,
        closeTimeout: 2000,
      }).open();
    } catch (err) {
      console.error("Save scrip failed:", err);
      f7.dialog.alert(err.message || "Failed to update scrip", "Save Failed");
    } finally {
      savingRowId = null;
    }
  }

  function toggleScripStatus(row) {
    row.trading_st = row.trading_st === 1 ? 0 : 1;
    companyScrips = [...companyScrips];
    saveScripMatrixRow(row);
  }

  async function emergencySquareOffScrip(symbol) {
    if (!symbol || symbol === "-") return;
    f7.dialog.confirm(
      `⚠️ EMERGENCY: Are you sure you want to SQUARE OFF all open positions for ${symbol} across ALL clients? This will fire immediate market exit orders.`,
      `Square Off ${symbol}`,
      async () => {
        try {
          f7.dialog.preloader(`Squaring off ${symbol} across all clients...`);
          await TradingService.squareOffScrip(symbol);
          f7.dialog.close();
          f7.dialog.alert(
            `Square-off orders dispatched for ${symbol}. Client positions are being closed by the execution engine.`,
            "Square-Off Dispatched"
          );
        } catch (err) {
          f7.dialog.close();
          console.error(`Failed to square off ${symbol}:`, err);
          f7.dialog.alert(err.message || `Square off failed for ${symbol}`, "Execution Error");
        }
      }
    );
  }

  onMount(() => {
    loadCompanyArray();
    loadCatalogScrips();
  });

  // Reactive filters for /s3s4 Matrix
  $: filteredCompanyScrips = companyScrips.filter((s) => {
    const q = searchQuery.toLowerCase().trim();
    const matchQuery =
      !q ||
      s.instrument.toLowerCase().includes(q) ||
      s.name.toLowerCase().includes(q) ||
      String(s.bse_token).includes(q);

    if (!matchQuery) return false;
    if (statusFilter === "active") return s.trading_st === 1;
    if (statusFilter === "disabled") return s.trading_st === 0;
    return true;
  });

  // Reactive filters for Catalog
  $: filteredCatalogScrips = scrips.filter((s) => {
    const q = searchQuery.toLowerCase().trim();
    return (
      !q ||
      (s.name && s.name.toLowerCase().includes(q)) ||
      (s.symbol && s.symbol.toLowerCase().includes(q)) ||
      String(s.bse_token).includes(q)
    );
  });
</script>

<Page name="master-data-manager">
  <TradingNavbar title="Master Scrip &amp; Strategy Matrix" />

  <!-- TAB SWITCHER NAV BAR -->
  <div class="tabs-nav-bar">
    <button
      type="button"
      class={`tab-pill ${activeTab === 's3s4' ? 'active' : ''}`}
      on:click={() => (activeTab = 's3s4')}
    >
      ⚡ Strategy Matrix (/s3s4 Parity)
      <span class="pill-badge">{companyScrips.length}</span>
    </button>
    <button
      type="button"
      class={`tab-pill ${activeTab === 'tokens' ? 'active' : ''}`}
      on:click={() => (activeTab = 'tokens')}
    >
      📖 Master Token Directory
      <span class="pill-badge">{scrips.length}</span>
    </button>
  </div>

  <Searchbar
    customSearch={true}
    placeholder={activeTab === 's3s4'
      ? 'Search Strategy Matrix by Symbol (RELIANCE) or BSE Code (500325)...'
      : 'Search Master Catalog by Name, Symbol or BSE Token...'}
    value={searchQuery}
    onInput={(e) => (searchQuery = e.target.value)}
    onSearchbarClear={() => (searchQuery = "")}
  />

  <Block>
    <!-- TAB 1: /s3s4 STRATEGY MATRIX (F1F2 AUTHENTIC PARITY) -->
    {#if activeTab === "s3s4"}
      {#if companyArrayError}
        <Card class="bg-color-red text-color-white margin-bottom">
          <CardContent>
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <div>
                <strong>⚠️ Strategy Matrix API Error</strong>
                <div style="font-size: 13px; margin-top: 4px;">{companyArrayError}</div>
              </div>
              <Button fill small color="white" text-color="red" onClick={loadCompanyArray}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      {/if}

      <!-- Quick Filter Bar -->
      <div class="filter-toolbar margin-bottom">
        <div class="filter-group">
          <span class="filter-label">Filter:</span>
          <button
            type="button"
            class={`filter-chip ${statusFilter === 'all' ? 'chip-active' : ''}`}
            on:click={() => (statusFilter = 'all')}
          >
            All Scrips ({companyScrips.length})
          </button>
          <button
            type="button"
            class={`filter-chip ${statusFilter === 'active' ? 'chip-active' : ''}`}
            on:click={() => (statusFilter = 'active')}
          >
            🟢 Active Only ({companyScrips.filter(s => s.trading_st === 1).length})
          </button>
          <button
            type="button"
            class={`filter-chip ${statusFilter === 'disabled' ? 'chip-active' : ''}`}
            on:click={() => (statusFilter = 'disabled')}
          >
            🔴 Disabled ({companyScrips.filter(s => s.trading_st === 0).length})
          </button>
        </div>
        <Button small outline color="blue" onClick={loadCompanyArray}>
          <Icon f7="arrow_clockwise" size="14" /> &nbsp; Refresh Matrix
        </Button>
      </div>

      <Card class="data-table">
        <CardHeader class="display-flex justify-content-space-between align-items-center">
          <div>
            <strong>F1F2 Company Array Matrix (`f1f2_company_array`)</strong>
            <span style="font-size: 12px; color: #64748b; margin-left: 8px;">
              Showing {filteredCompanyScrips.length} of {companyScrips.length} scrips
            </span>
          </div>
          <span class="badge color-green" style="font-size: 11px;">
            Hot-Reload Enabled (Engine syncs in &lt;1ms)
          </span>
        </CardHeader>
        <CardContent padding={false}>
          <div class="matrix-table-wrap">
            <table class="matrix-table">
              <thead>
                <tr>
                  <th style="min-width: 140px;">Scrip / Symbol</th>
                  <th style="width: 90px; text-align: center;">Trading Status</th>
                  <th style="min-width: 190px;">Base Lots (H / M / L)</th>
                  <th style="min-width: 130px;">Max Dips (B / S)</th>
                  <th style="min-width: 130px;">Target / SL %</th>
                  <th style="min-width: 90px;">Trailing SL %</th>
                  <th style="min-width: 170px; text-align: right;">Actions</th>
                </tr>
              </thead>
              <tbody>
                {#if filteredCompanyScrips.length === 0}
                  <tr>
                    <td colspan="7" style="text-align: center; padding: 32px; color: #94a3b8;">
                      {isLoadingCompanyArray ? "Loading F1F2 scrip matrix..." : "No matching scrips found"}
                    </td>
                  </tr>
                {:else}
                  {#each filteredCompanyScrips as row (row.id)}
                    <tr class={row.trading_st === 0 ? "row-disabled" : ""}>
                      <!-- Scrip & BSE Code -->
                      <td>
                        <div class="scrip-symbol-text">{row.instrument}</div>
                        <div class="scrip-sub-text">BSE: <strong>{row.bse_token || '-'}</strong></div>
                      </td>

                      <!-- Trading Status Toggle -->
                      <td style="text-align: center;">
                        <button
                          type="button"
                          class={`status-toggle-btn ${row.trading_st === 1 ? 'st-active' : 'st-off'}`}
                          on:click={() => toggleScripStatus(row)}
                        >
                          {row.trading_st === 1 ? '🟢 ACTIVE' : '🔴 OFF'}
                        </button>
                      </td>

                      <!-- Base Lots H / M / L -->
                      <td>
                        <div class="lot-inputs-group">
                          <div class="lot-field">
                            <span class="lot-label lot-h">H</span>
                            <input
                              type="number"
                              min="1"
                              class="table-cell-input"
                              bind:value={row.H_qty}
                            />
                          </div>
                          <div class="lot-field">
                            <span class="lot-label lot-m">M</span>
                            <input
                              type="number"
                              min="1"
                              class="table-cell-input"
                              bind:value={row.M_qty}
                            />
                          </div>
                          <div class="lot-field">
                            <span class="lot-label lot-l">L</span>
                            <input
                              type="number"
                              min="1"
                              class="table-cell-input"
                              bind:value={row.L_qty}
                            />
                          </div>
                        </div>
                      </td>

                      <!-- Max Dips (bqty_st / sqty_st) -->
                      <td>
                        <div class="dips-group">
                          <div class="lot-field">
                            <span class="dips-label">Buy</span>
                            <input
                              type="number"
                              min="0"
                              max="10"
                              class="table-cell-input-sm"
                              bind:value={row.bqty_st}
                            />
                          </div>
                          <div class="lot-field">
                            <span class="dips-label">Sell</span>
                            <input
                              type="number"
                              min="0"
                              max="10"
                              class="table-cell-input-sm"
                              bind:value={row.sqty_st}
                            />
                          </div>
                        </div>
                      </td>

                      <!-- Target % / Stop Loss % -->
                      <td>
                        <div class="pct-group">
                          <div class="lot-field">
                            <span class="pct-label pct-target">Tgt%</span>
                            <input
                              type="number"
                              step="0.1"
                              min="0.1"
                              class="table-cell-input-sm"
                              bind:value={row.buy_target_intra}
                            />
                          </div>
                          <div class="lot-field">
                            <span class="pct-label pct-sl">SL%</span>
                            <input
                              type="number"
                              step="0.1"
                              min="0.1"
                              class="table-cell-input-sm"
                              bind:value={row.buy_stl_intra}
                            />
                          </div>
                        </div>
                      </td>

                      <!-- Trailing SL % -->
                      <td>
                        <div class="lot-field">
                          <span class="pct-label pct-tsl">TSL%</span>
                          <input
                            type="number"
                            step="0.05"
                            min="0"
                            class="table-cell-input-sm"
                            bind:value={row.Trailing_Stoploss}
                          />
                        </div>
                      </td>

                      <!-- Actions: Save & Emergency Square-Off -->
                      <td style="text-align: right;">
                        <div class="action-btn-group">
                          <button
                            type="button"
                            class="btn-chart-scrip"
                            title="View live candlestick and signal chart"
                            on:click={() => openScripChart(row.instrument, row.bse_token)}
                          >
                            📈 Chart
                          </button>
                          <button
                            type="button"
                            class={`btn-save-row ${savingRowId === row.id ? 'is-saving' : ''}`}
                            disabled={savingRowId === row.id || !isRowModified(row, row.H_qty, row.M_qty, row.L_qty, row.bqty_st, row.sqty_st, row.buy_target_intra, row.buy_stl_intra, row.Trailing_Stoploss)}
                            title={isRowModified(row, row.H_qty, row.M_qty, row.L_qty, row.bqty_st, row.sqty_st, row.buy_target_intra, row.buy_stl_intra, row.Trailing_Stoploss) ? "Save changes to this scrip" : "No modifications to save"}
                            on:click={() => saveScripMatrixRow(row)}
                          >
                            {savingRowId === row.id ? "Saving..." : "💾 Save"}
                          </button>
                          <button
                            type="button"
                            class="btn-emergency-sqr"
                            title="Emergency Square-Off all clients holding this scrip"
                            on:click={() => emergencySquareOffScrip(row.instrument)}
                          >
                            ⚡ Sqr All
                          </button>
                        </div>
                      </td>
                    </tr>
                  {/each}
                {/if}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    {/if}

    <!-- TAB 2: MASTER TOKEN DIRECTORY -->
    {#if activeTab === "tokens"}
      {#if catalogError}
        <Card class="bg-color-red text-color-white margin-bottom">
          <CardContent>
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <div>
                <strong>⚠️ Scrip Catalog API Error</strong>
                <div style="font-size: 13px; margin-top: 4px;">{catalogError}</div>
              </div>
              <Button fill small color="white" text-color="red" onClick={loadCatalogScrips}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      {/if}

      <Card class="data-table">
        <CardHeader>Master Scrip Catalog ({filteredCatalogScrips.length} matched)</CardHeader>
        <CardContent padding={false}>
          <table>
            <thead>
              <tr>
                <th class="label-cell">BSE Token</th>
                <th class="label-cell">Symbol</th>
                <th class="label-cell">Company Name</th>
                <th class="label-cell">Category / Sector</th>
                <th class="numeric-cell">Internal Token</th>
              </tr>
            </thead>
            <tbody>
              {#if filteredCatalogScrips.length === 0}
                <tr>
                  <td colspan="5" style="text-align: center; padding: 24px; color: #94a3b8;">
                    {isLoadingCatalog ? "Loading master catalog..." : "No matching scrips found"}
                  </td>
                </tr>
              {:else}
                {#each filteredCatalogScrips as scrip}
                  <tr>
                    <td class="label-cell font-bold">{scrip.bse_token}</td>
                    <td class="label-cell">{scrip.symbol}</td>
                    <td class="label-cell">{scrip.name}</td>
                    <td class="label-cell"><span class="badge color-blue">{scrip.category}</span></td>
                    <td class="numeric-cell">{scrip.token}</td>
                  </tr>
                {/each}
              {/if}
            </tbody>
          </table>
        </CardContent>
      </Card>
    {/if}
  </Block>

  <LiveSignalChartModal
    isOpen={showChartModal}
    symbol={chartSymbol}
    scripCode={chartScripCode}
    onClose={() => (showChartModal = false)}
  />
</Page>

<style>
  .tabs-nav-bar {
    display: flex;
    gap: 8px;
    padding: 12px 16px 4px 16px;
    background: #ffffff;
    border-bottom: 1px solid #e2e8f0;
  }
  .tab-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 6px;
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    color: #475569;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .tab-pill:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
  }
  .tab-pill.active {
    background: #1e293b;
    color: #ffffff;
    border-color: #0f172a;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  .pill-badge {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    padding: 2px 6px;
    font-size: 10px;
  }
  .tab-pill:not(.active) .pill-badge {
    background: #e2e8f0;
    color: #475569;
  }

  /* Filter toolbar */
  .filter-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .filter-group {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  .filter-label {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
  }
  .filter-chip {
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #475569;
    border-radius: 16px;
    padding: 4px 12px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .filter-chip:hover {
    background: #f1f5f9;
  }
  .filter-chip.chip-active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
  }

  /* /s3s4 Matrix Table Styling */
  .matrix-table-wrap {
    overflow-x: auto;
    width: 100%;
  }
  .matrix-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    text-align: left;
  }
  .matrix-table th {
    background: #f8fafc;
    color: #475569;
    padding: 10px 12px;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
  }
  .matrix-table td {
    padding: 8px 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }
  .matrix-table tr:hover {
    background: #f8fafc;
  }
  .matrix-table tr.row-disabled {
    background: rgba(241, 245, 249, 0.7);
    opacity: 0.75;
  }

  .scrip-symbol-text {
    font-weight: 800;
    font-size: 13px;
    color: #0f172a;
  }
  .scrip-sub-text {
    font-size: 11px;
    color: #64748b;
  }

  .status-toggle-btn {
    border: 1px solid;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
  }
  .status-toggle-btn.st-active {
    background: #dcfce7;
    color: #15803d;
    border-color: #86efac;
  }
  .status-toggle-btn.st-active:hover {
    background: #bbf7d0;
  }
  .status-toggle-btn.st-off {
    background: #fee2e2;
    color: #b91c1c;
    border-color: #fca5a5;
  }
  .status-toggle-btn.st-off:hover {
    background: #fecaca;
  }

  /* Inline Lot Inputs */
  .lot-inputs-group,
  .dips-group,
  .pct-group {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .lot-field {
    display: flex;
    align-items: center;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    overflow: hidden;
    background: #ffffff;
  }
  .lot-label {
    padding: 3px 6px;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
  }
  .lot-label.lot-h { background: #fee2e2; color: #b91c1c; }
  .lot-label.lot-m { background: #fef3c7; color: #b45309; }
  .lot-label.lot-l { background: #dcfce7; color: #15803d; }

  .dips-label {
    padding: 3px 5px;
    font-size: 10px;
    font-weight: 700;
    background: #f1f5f9;
    color: #475569;
  }

  .pct-label {
    padding: 3px 5px;
    font-size: 10px;
    font-weight: 700;
  }
  .pct-label.pct-target { background: #dcfce7; color: #15803d; }
  .pct-label.pct-sl { background: #fee2e2; color: #b91c1c; }
  .pct-label.pct-tsl { background: #e0e7ff; color: #4338ca; }

  .table-cell-input {
    width: 44px;
    padding: 3px 4px;
    font-size: 12px;
    font-weight: 700;
    font-family: monospace;
    border: none;
    outline: none;
    text-align: center;
    color: #0f172a;
  }
  .table-cell-input-sm {
    width: 38px;
    padding: 3px 3px;
    font-size: 11px;
    font-weight: 700;
    font-family: monospace;
    border: none;
    outline: none;
    text-align: center;
    color: #0f172a;
  }

  .action-btn-group {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
  }
  .btn-save-row {
    background: #2563eb;
    color: #ffffff;
    border: 1px solid #2563eb;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
  }
  .btn-chart-scrip {
    background: #0ea5e9;
    color: #ffffff;
    border: none;
    padding: 5px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
  }
  .btn-chart-scrip:hover {
    background: #0284c7;
  }
  .btn-save-row:hover:not(:disabled) {
    background: #1d4ed8;
    border-color: #1d4ed8;
  }
  .btn-save-row:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    border: 1px solid #cbd5e1;
    cursor: not-allowed;
    opacity: 1;
  }
  .btn-save-row.is-saving:disabled {
    background: #93c5fd;
    color: #1e40af;
    border-color: #93c5fd;
  }

  .btn-emergency-sqr {
    background: #fff1f2;
    color: #e11d48;
    border: 1px solid #fda4af;
    padding: 5px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
  }
  .btn-emergency-sqr:hover {
    background: #ffe4e6;
    border-color: #f43f5e;
  }
</style>
